import {
  configuredYoutubePlaceholderSlider,
  configuredYoutubeSlider,
  configuredYoutubeVideo,
} from "./media";
import { demoCampaignContent } from "../demo-content";
import { safeHttpUrl } from "./safe-url";

const SUPPORTED_PHASES = new Set([1, 2, 3]);

function validPhase(value) {
  const phase = Number(value);
  return SUPPORTED_PHASES.has(phase) ? phase : null;
}

export function resolveCampaignPhase(config, location = window.location) {
  const queryPhase = new URLSearchParams(location?.search ?? "").get("phase");
  const requestedPhase = validPhase(queryPhase);
  if (requestedPhase) return requestedPhase;

  return validPhase(config.activePhase) ?? 1;
}

export function createCampaignRuntimeConfig(
  config,
  location = window.location,
) {
  const activePhase = resolveCampaignPhase(config, location);
  const queryPhase = validPhase(
    new URLSearchParams(location?.search ?? "").get("phase"),
  );
  const demoRequested = Boolean(queryPhase && activePhase > 1);
  const configuredHeader = configuredYoutubeVideo(config.youtube?.header);
  const configuredSlider = configuredYoutubeSlider(config.youtube?.slider);
  const configuredStories = configuredYoutubeSlider(config.youtube?.stories);
  const configuredWinners = winnersConfigured(config.winners);
  const usesDemoYoutube = demoRequested
    && activePhase >= 2
    && (!configuredHeader || !configuredSlider);
  const usesDemoWinners = demoRequested
    && activePhase === 3
    && !configuredWinners;

  return {
    ...config,
    activePhase,
    demoMode: usesDemoYoutube || usesDemoWinners,
    youtube: {
      header: configuredHeader
        ? config.youtube.header
        : (usesDemoYoutube ? demoCampaignContent.youtube.header : null),
      slider: configuredSlider
        ? config.youtube.slider
        : (usesDemoYoutube
          ? demoCampaignContent.youtube.slider
          : (config.youtube?.slider ?? { title: null, videos: [] })),
      stories: configuredStories
        ? config.youtube.stories
        : (usesDemoYoutube
          ? demoCampaignContent.youtube.stories
          : { title: null, videos: [] }),
    },
    winners: configuredWinners
      ? config.winners
      : (usesDemoWinners ? demoCampaignContent.winners : null),
  };
}

function winnersConfigured(winners) {
  return Boolean(
    winners?.grandPrize
    && Array.isArray(winners.secondaryPrizes)
    && winners.secondaryPrizes.length > 0,
  );
}

function configuredModules(config, activePhase) {
  const phase = String(activePhase);
  const requested = config.phaseModules?.[phase] ?? config.phaseModules?.[1] ?? [];
  const modules = new Set(requested);

  if (modules.has("hero-video") && !configuredYoutubeVideo(config.youtube?.header)) {
    modules.delete("hero-video");
    modules.add("hero-default");
  }

  if (
    modules.has("villancicos")
    && !configuredYoutubeSlider(config.youtube?.slider)
    && !configuredYoutubePlaceholderSlider(config.youtube?.slider)
  ) {
    modules.delete("villancicos");
  }

  if (modules.has("stories") && !configuredYoutubeSlider(config.youtube?.stories)) {
    modules.delete("stories");
  }

  if (modules.has("winners") && !winnersConfigured(config.winners)) {
    modules.delete("winners");
  }

  return modules;
}

function configureExternalLinks(config) {
  const crmUrl = safeHttpUrl(config.crmUrl, { allowAnyHttps: true });
  if (crmUrl) {
    document.querySelectorAll("[data-crm-link]").forEach((control) => {
      const link = document.createElement("a");
      link.className = control.className;
      link.append(...Array.from(control.childNodes, (node) => node.cloneNode(true)));
      link.href = crmUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.dataset.crmLink = "";
      control.replaceWith(link);
    });
  }

  document.querySelectorAll("[data-legal-link]").forEach((label) => {
    const key = label.dataset.legalLink;
    const url = config.legalUrls?.[key];
    const safeUrl = safeHttpUrl(url, { allowAnyHttps: true });
    if (!safeUrl) return;

    const link = document.createElement("a");
    link.href = safeUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = label.textContent;
    label.replaceWith(link);
  });
}

export function initCampaign(config) {
  const activePhase = resolveCampaignPhase(config);
  const activeModules = configuredModules(config, activePhase);
  document.body.dataset.campaignPhase = String(activePhase);
  document.body.dataset.campaignContent = config.demoMode ? "demo" : "configured";

  document.querySelectorAll("[data-campaign-module]").forEach((module) => {
    const active = activeModules.has(module.dataset.campaignModule);
    module.hidden = !active;
    module.dataset.campaignModuleState = active ? "active" : "inactive";
  });

  document.querySelectorAll("[data-campaign-module-link]").forEach((item) => {
    const allowedPhases = (item.dataset.campaignPhaseLink || "")
      .split(",")
      .map(validPhase)
      .filter(Boolean);
    const phaseAllowed = allowedPhases.length === 0 || allowedPhases.includes(activePhase);
    item.hidden = !phaseAllowed
      || !activeModules.has(item.dataset.campaignModuleLink);
  });

  document.querySelectorAll("[data-phase-sorteo-link]").forEach((item) => {
    const link = item.querySelector("a");
    if (!link) return;

    link.href = activePhase === 3 ? "#ganadores" : "#sorteo";
    link.textContent = "Sorteo";
  });

  configureExternalLinks(config);
  return activeModules;
}
